import { Injectable } from "@angular/core";
import { ProspectLoginDTO, ProspectSignupDTO } from "../_models/prospect-dtos";
import { RedacteurDTO } from "../_models/redacteur-dto";
import { HttpClient } from "@angular/common/http";
import { PageService } from "./page.service";
import { Router } from "@angular/router";
import { MatDialog } from "@angular/material/dialog";
import { GlobaldataService } from "./globaldata.service";
import { tap } from "rxjs/operators";
import { environment } from "src/environments/environment";

@Injectable({
  providedIn: "root",
})
export class AuthService {
  constructor(
    private pageService: PageService,
    private router: Router,
    private dialogRef: MatDialog,
    private userData: GlobaldataService,
    private http: HttpClient
  ) {}

  /**
   * ✅ CORRIGÉ : Récupère l'utilisateur depuis localStorage
   * Supporte deux formats :
   * 1. Format moderne : clé 'currentUser' (JSON)
   * 2. Format legacy : clés ACTIVA_* séparées
   */
  getCurrentUser() {
    console.log('[DEBUG] AuthService.getCurrentUser - Début');
    
    // ✅ MÉTHODE 1 : Essayer de récupérer depuis 'currentUser'
    const userStr = localStorage.getItem('currentUser');
    
    if (userStr) {
      console.log('[DEBUG] Format moderne détecté (currentUser)');
      try {
        const user = JSON.parse(userStr);
        console.log('[DEBUG] ✅ Utilisateur récupéré (moderne)********************************:', user);
        console.log('[DEBUG] ProfilCode :', user?.profilCode);
        console.log('[DEBUG] Point de vente:', user?.pointofsale);
        console.log('[DEBUG] Facturation obligatoire :', user?.isbillingcompulsory);
        return user;
      } catch (error) {
        console.error('[ERROR] Erreur lors du parsing de currentUser:', error);
        // Continue vers MÉTHODE 2
      }
    }
    
    // ✅ MÉTHODE 2 : Fallback - Construire depuis les clés ACTIVA_* (legacy)
    console.log('[DEBUG] Format legacy détecté (clés ACTIVA_*)');
    
    try {
      const username = localStorage.getItem('ACTIVA_REDACTEUR_USERNAME');
      const firstname = localStorage.getItem('ACTIVA_REDACTEUR_FIRSTNAME');
      const lastname = localStorage.getItem('ACTIVA_REDACTEUR_LASTNAME');
      const email = localStorage.getItem('ACTIVA_REDACTEUR_EMAIL');
      const phone = localStorage.getItem('ACTIVA_REDACTEUR_PHONE');
      const profilCode = localStorage.getItem('ACTIVA_PROFILECODE');
      const pointofsaleStr = localStorage.getItem('ACTIVA_REDACTEUR_POS_CODE');
      const token = localStorage.getItem('ACTIVA_REDACTEUR_TOKEN');
      const isAdminStr = localStorage.getItem('ACTIVA_REDACTEUR_IS_ADMIN'); // Si disponible
      const isbillingcompulsory = localStorage.getItem('ACTIVA_REDACTEUR_ISBILLINGCOMPULSORY');
      
      console.log('[DEBUG] Clés récupérées:');
      console.log('  - username:', username);
      console.log('  - profilCode:', profilCode);
      console.log('  - pointofsale:', pointofsaleStr);
      
      // ✅ Vérifier que les données essentielles existent
      if (!username || !profilCode || !pointofsaleStr) {
        console.error('[ERROR] ❌ Données essentielles manquantes dans localStorage');
        console.error('[ERROR] Disponibles: username:', !!username, 'profilCode:', !!profilCode, 'pointofsale:', !!pointofsaleStr);
        return null;
      }
      
      // ✅ Convertir pointofsale en nombre
      const pointofsale = parseInt(pointofsaleStr, 10);
      
      if (isNaN(pointofsale)) {
        console.error('[ERROR] ❌ pointofsale n\'est pas un nombre valide:', pointofsaleStr);
        return null;
      }
      
      // ✅ Construire l'objet utilisateur
      const user = {
        username: username,
        firstname: firstname || '',
        lastname: lastname || '',
        email: email || '',
        phone: phone || '',
        profilCode: profilCode,
        pointofsale: pointofsale, // ✅ Nombre
        token: token || '',
        isAdmin: isAdminStr === 'true' || false,
        isbillingcompulsory: isbillingcompulsory === 'true' || false
      };
      
      console.log('[DEBUG] ✅ Utilisateur construit (legacy):', user);
      console.log('[DEBUG] Type de pointofsale:', typeof user.pointofsale);
      
      // ✅ IMPORTANT : Sauvegarder dans le format moderne pour la prochaine fois
      localStorage.setItem('currentUser', JSON.stringify(user));
      console.log('[DEBUG] ✅ Utilisateur migré vers format moderne');
      
      return user;
      
    } catch (error) {
      console.error('[ERROR] ❌ Exception dans getCurrentUser:', error);
      return null;
    }
  }

  /**
   * ✅ CORRIGÉ : Récupère le code profil
   */
  getprofilCode(): string | null {
    // Essayer d'abord depuis currentUser
    const user = this.getCurrentUser();
    if (user?.profilCode) {
      return user.profilCode;
    }
    
    // Fallback : clé ACTIVA_PROFILECODE (sans espace !)
    const profilecode = localStorage.getItem('ACTIVA_PROFILECODE');
    console.log('[DEBUG] AuthService.getprofilCode:', profilecode);
    return profilecode;
  }

  /**
   * Vérifier si l'utilisateur est un admin point de vente
   */
  isPointOfAdmin(): boolean {
    const user = this.getCurrentUser();
    return user?.isPointOfAdmin === true || user?.profilCode === 'profile_code_2';
  }

  /**
   * Récupérer le point de vente de l'utilisateur connecté
   */
  getCurrentUserPointOfSale(): number | null {
    const user = this.getCurrentUser();
    
    if (!user) {
      console.error('[ERROR] getCurrentUserPointOfSale - Aucun utilisateur');
      return null;
    }
    
    const pos = user.pointofsale;
    console.log('[DEBUG] getCurrentUserPointOfSale:', pos, 'type:', typeof pos);
    
    return typeof pos === 'number' ? pos : (pos ? parseInt(pos, 10) : null);
  }

  /**
   * Vérifier si l'utilisateur est connecté
   */
  get isLoggedIn(): boolean {
    return !!this.getCurrentUser();
  }

  /**
   * Check if prospect is logged-in
   */
  get isProspectLogin() {
    const prosId = localStorage.getItem("ACTIVA_REDACTEUR_USERNAME");
    return Boolean(prosId);
  }

  /**
   * Check if the password needs to be updated
   */
  get isPasswordExpired() {
    const prosId = localStorage.getItem("ACTIVA_REDACTEUR_IS_PASSWORD_EXPIRED");
    return Boolean(prosId);
  }

  /**
   * Login prospect
   */
  login(prospect: ProspectLoginDTO) {
    const phone = prospect.prospect_phone;
    return this.http.post(`${environment.loginUrl}/login/prospect`, { phone });
  }
  
  loginX(phone: string) {
    return this.http.post(`${environment.loginUrl}/login/prospect`, { phone });
  }

  /**
   * Register prospect
   */
  register(prospect: ProspectSignupDTO) {
    const user = {
      name: prospect.prospect_name,
      email: prospect.prospect_email,
      phone: prospect.prospect_phone,
    };
    return this.http.post(`${environment.loginUrl}/register/prospect`, user);
  }

  /**
   * ✅ CORRIGÉ : Logout avec nettoyage complet
   */
  logout() {
    console.log('[DEBUG] AuthService.logout - Début');
    
    // ✅ Nettoyer toutes les clés liées à l'authentification
   /* const keysToRemove = [
      'currentUser',
      'token',
      'ACTIVA_REDACTEUR_USERNAME',
      'ACTIVA_REDACTEUR_FIRSTNAME',
      'ACTIVA_REDACTEUR_LASTNAME',
      'ACTIVA_REDACTEUR_EMAIL',
      'ACTIVA_REDACTEUR_PHONE',
      'ACTIVA_PROFILECODE',
      'ACTIVA_REDACTEUR_POS_CODE',loginRedacteur
      'ACTIVA_REDACTEUR_POS_NAME',
      'ACTIVA_REDACTEUR_TOKEN',
      'ACTIVA_REDACTEUR_CODE_APPORTEUR',
      'ACTIVA_REDACTEUR_LAST_LOGIN_TIME',
      'ACTIVA_REDACTEUR_IS_PASSWORD_EXPIRED',
      'ACTIVA_REDACTEUR_IS_FIRST_PASSWORD_UPDATED',
      'ACTIVA_REDACTEUR_IS_ADMIN'
    ];
    
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
    });*/
    
    console.log('[DEBUG] ✅ localStorage nettoyé');
    
    // Rediriger
    this.router.navigate(["/welcome/thankyou"]);
  }

  /**
   * ✅ CORRIGÉ : Login rédacteur avec sauvegarde complète
   */
  loginRedacteur(redacteur: RedacteurDTO) {
    const username = redacteur.redacteur_username;
    const password = redacteur.redacteur_password;
    const fromAdmin = true;
    
    console.log("========================================");
    console.log("[DEBUG] loginRedacteur - Appel API");
    console.log("[DEBUG] Username:", username);
    
    return this.http.post(`${environment.loginUrl}/user/login`, { 
      username, 
      password, 
      fromAdmin 
    }).pipe(
      tap((response: any) => {
        if (response && response.requiresOtp) {
          return;
        }

        this.persistLoginResponse(response);
      })
    );
  }

  verifyLoginOtp(challengeId: string, code: string) {
    return this.http
      .post(`${environment.loginUrl}/user/login/verify-otp`, {
        challengeId,
        code,
      })
      .pipe(tap((response: any) => this.persistLoginResponse(response)));
  }

  private persistLoginResponse(response: any) {
    if (!response) {
      console.error('[ERROR] ❌ Réponse vide du backend');
      return;
    }

    console.log('========================================');
    console.log('[DEBUG] ✅ Réponse du backend reçue');
    console.log('[DEBUG] Structure de la réponse:', Object.keys(response));

    const user = response.user || response;
    const posCode = user?.pos?.code ?? user?.pointofsale ?? user?.pointOfSale ?? null;
    const normalizedPointOfSale = typeof posCode === 'number'
      ? posCode
      : posCode !== null && posCode !== undefined && posCode !== ''
        ? parseInt(posCode, 10)
        : null;

    const normalizedUser = {
      username: user?.username || '',
      firstname: user?.firstname || user?.firstName || '',
      lastname: user?.lastname || user?.lastName || '',
      email: user?.email || '',
      phone: user?.phone || '',
      profilCode: user?.profilCode || user?.profil || '',
      pointofsale: normalizedPointOfSale,
      isAdmin: Boolean(user?.isAdmin ?? user?.is_admin ?? false),
      token: response.token || user?.token || '',
      isbillingcompulsory: Boolean(user?.isbillingcompulsory ?? user?.isBillingCompulsory ?? false),
      bankId: user?.bankId ?? null,
      insuranceCompanyId: user?.insuranceCompanyId ?? null,
      ispasswordexpired: Boolean(user?.ispasswordexpired ?? user?.isPasswordExpired ?? false),
      isfirstpasswordupdated: Boolean(user?.isfirstpasswordupdated ?? user?.isFirstPasswordUpdated ?? false)
    };

    console.log('[DEBUG] ✅ Utilisateur normalisé:', normalizedUser);
    console.log('[DEBUG] Type de pointofsale:', typeof normalizedUser.pointofsale);
    console.log('[DEBUG] Facturation obligatoire oui ou non ? :', normalizedUser.isbillingcompulsory);

    if (!normalizedUser.username) {
      console.error('[ERROR] ❌ Username manquant');
    }
    if (!normalizedUser.profilCode) {
      console.error('[ERROR] ❌ ProfilCode manquant');
    }
    if (normalizedUser.pointofsale === null || isNaN(normalizedUser.pointofsale)) {
      console.error('[ERROR] ❌ Point de vente invalide:', posCode);
    }

    localStorage.setItem('currentUser', JSON.stringify(normalizedUser));
    console.log('[DEBUG] ✅ currentUser sauvegardé');

    if (normalizedUser.token) {
      localStorage.setItem('token', normalizedUser.token);
      console.log('[DEBUG] ✅ Token sauvegardé');
    }

    localStorage.setItem('ACTIVA_REDACTEUR_USERNAME', normalizedUser.username);
    localStorage.setItem('ACTIVA_REDACTEUR_FIRSTNAME', normalizedUser.firstname);
    localStorage.setItem('ACTIVA_REDACTEUR_LASTNAME', normalizedUser.lastname);
    localStorage.setItem('ACTIVA_REDACTEUR_EMAIL', normalizedUser.email);
    localStorage.setItem('ACTIVA_REDACTEUR_PHONE', normalizedUser.phone);
    localStorage.setItem('ACTIVA_PROFILECODE', normalizedUser.profilCode);
    localStorage.setItem('ACTIVA_REDACTEUR_POS_CODE', normalizedUser.pointofsale !== null ? normalizedUser.pointofsale.toString() : '');
    localStorage.setItem('ACTIVA_REDACTEUR_TOKEN', normalizedUser.token);
    localStorage.setItem('ACTIVA_REDACTEUR_IS_ADMIN', normalizedUser.isAdmin ? 'true' : 'false');
    localStorage.setItem('ACTIVA_REDACTEUR_ISBILLINGCOMPULSORY', normalizedUser.isbillingcompulsory ? 'true' : 'false');
    localStorage.setItem('ACTIVA_REDACTEUR_IS_PASSWORD_EXPIRED', normalizedUser.ispasswordexpired ? 'true' : 'false');
    localStorage.setItem('ACTIVA_REDACTEUR_IS_FIRST_PASSWORD_UPDATED', normalizedUser.isfirstpasswordupdated ? 'true' : 'false');

    console.log('[DEBUG] ✅ Clés ACTIVA_* sauvegardées (legacy)');

    const verification = localStorage.getItem('currentUser');
    console.log('[DEBUG] ✅ Vérification - currentUser existe:', !!verification);

    console.log('========================================');
  }
}
